import { z } from 'zod';
import { formApplies, isClinicalFormCode } from './clinical-forms';
export const EMR_CONTEXTS = ['opd', 'ipd'];
export const EMR_FORM_KINDS = ['section', 'clinical_form', 'order'];
export const EMR_FORM_GROUPS = ['section', 'ipd_form', 'order', 'clinical_form'];
export const EMR_FORM_LABEL_NAMESPACE = 'opd';
const ON = { enabled: true, required: false };
const REQUIRED = { enabled: true, required: true };
function form(key, label, kind, defaults, options = {}) {
    return {
        key,
        labelKey: `emr_form_${key}`,
        label,
        kind,
        contexts: EMR_CONTEXTS.filter((context) => defaults[context] !== undefined),
        defaults,
        lockedRequired: options.lockedRequired ?? false,
        clinicalFormCode: options.clinicalFormCode ?? null,
    };
}
const opdSection = (key, label, setting = ON, lockedRequired = false) => form(key, label, 'section', { opd: setting }, { lockedRequired });
const ipdForm = (key, label, kind, setting = ON) => form(key, label, kind, { ipd: setting });
const orderForm = (key, label) => form(key, label, 'order', { opd: ON, ipd: ON });
const clinicalForm = (code, label) => form(code, label, 'clinical_form', { opd: ON, ipd: ON }, { clinicalFormCode: code });
export const EMR_FORMS = [
    opdSection('vitals', 'Vitals'),
    opdSection('complaint_history', 'Complaint and history', REQUIRED),
    opdSection('review_of_systems', 'Review of systems'),
    opdSection('examination', 'Examination'),
    opdSection('diagnosis', 'Diagnosis', REQUIRED, true),
    opdSection('plan_follow_up', 'Plan and follow-up'),
    opdSection('prescription', 'Prescription'),
    ipdForm('progress_note', 'Progress note', 'section', REQUIRED),
    orderForm('lab_order', 'Lab order'),
    orderForm('radiology_order', 'Radiology order'),
    orderForm('procedure_order', 'Procedure order'),
    ipdForm('medication_order', 'Medication order', 'order'),
    ipdForm('iv_fluid_order', 'IV fluid order', 'order'),
    ipdForm('diet_order', 'Diet order', 'order'),
    ipdForm('nursing_order', 'Nursing order', 'order'),
    ipdForm('blood_transfusion_order', 'Blood transfusion order', 'order'),
    ipdForm('oxygen_order', 'Oxygen order', 'order'),
    ipdForm('physiotherapy_order', 'Physiotherapy order', 'order'),
    ipdForm('consult_order', 'Consult order', 'order'),
    ipdForm('vitals_monitoring_order', 'Vitals monitoring order', 'order'),
    ipdForm('discharge_summary', 'Discharge summary', 'section'),
    clinicalForm('obstetric_history', 'Obstetric history'),
    clinicalForm('menstrual_history', 'Menstrual history'),
    clinicalForm('paediatric_history', 'Paediatric history'),
    clinicalForm('immunisation_status', 'Immunisation status'),
    clinicalForm('growth_measurement', 'Growth measurement'),
    clinicalForm('procedure_note', 'Procedure note'),
    clinicalForm('patient_education', 'Patient education'),
    clinicalForm('pre_procedure_consent', 'Pre-procedure consent'),
];
export const EMR_FORM_KEYS = EMR_FORMS.map((definition) => definition.key);
const EMR_FORM_INDEX = new Map(EMR_FORMS.map((definition, index) => [definition.key, { definition, index }]));
export const EMR_SORT_STEP = 10;
export function emrFormDefinition(key) {
    return EMR_FORM_INDEX.get(key)?.definition;
}
export function isEmrFormKey(key) {
    return EMR_FORM_INDEX.has(key);
}
export function emrFormsFor(context, registry = EMR_FORMS) {
    return registry.filter((definition) => definition.contexts.includes(context));
}
export function emrFormGroup(definition) {
    if (definition.contexts.length === 1 && definition.contexts[0] === 'ipd')
        return 'ipd_form';
    return definition.kind;
}
export function emrContextOfEncounter(encounterContext) {
    return encounterContext === 'ipd' ? 'ipd' : 'opd';
}
function defaultSortOrders(context, registry) {
    return new Map(emrFormsFor(context, registry).map((definition, position) => [definition.key, (position + 1) * EMR_SORT_STEP]));
}
function byKey(rules) {
    return new Map(rules.map((rule) => [rule.formKey, rule]));
}
export function effectiveEmrForms(registry, hospitalRules, departmentRules, context) {
    const hospital = byKey(hospitalRules);
    const department = byKey(departmentRules);
    const defaultOrder = defaultSortOrders(context, registry);
    const position = new Map(registry.map((definition, index) => [definition.key, index]));
    const resolved = emrFormsFor(context, registry).map((definition) => {
        const fallback = definition.defaults[context] ?? ON;
        const rule = department.get(definition.key) ?? hospital.get(definition.key);
        const source = department.has(definition.key) ? 'department' : hospital.has(definition.key) ? 'hospital' : 'default';
        const enabled = definition.lockedRequired || (rule ? rule.enabled : fallback.enabled);
        const required = definition.lockedRequired || (enabled && (rule ? rule.required : fallback.required));
        return {
            key: definition.key,
            kind: definition.kind,
            enabled,
            required,
            sortOrder: rule ? rule.sortOrder : (defaultOrder.get(definition.key) ?? 0),
            source,
        };
    });
    return resolved.sort((a, b) => a.sortOrder - b.sortOrder || (position.get(a.key) ?? 0) - (position.get(b.key) ?? 0));
}
export function resolveEmrForms(registry, hospitalRules, departmentRules, context) {
    return effectiveEmrForms(registry, hospitalRules, departmentRules, context).map(({ source: _source, ...form }) => form);
}
export function emrFormAppliesToPatient(key, patient) {
    const definition = emrFormDefinition(key);
    if (!definition?.clinicalFormCode || !isClinicalFormCode(definition.clinicalFormCode))
        return true;
    return formApplies(definition.clinicalFormCode, patient);
}
export const emrContextSchema = z.enum(EMR_CONTEXTS);
export const emrFormConfigQuerySchema = z.object({
    context: emrContextSchema,
    departmentId: z.string().uuid().optional(),
});
export const emrFormRuleInputSchema = z.object({
    formKey: z.string().trim().min(1).max(60),
    enabled: z.boolean(),
    required: z.boolean(),
    sortOrder: z.number().int().min(0).max(100_000),
});
export const saveEmrFormRulesSchema = z.object({
    context: emrContextSchema,
    departmentId: z.string().uuid().nullable().default(null),
    rules: z.array(emrFormRuleInputSchema).max(EMR_FORMS.length).default([]),
    reset: z.array(z.string().trim().min(1).max(60)).max(EMR_FORMS.length).default([]),
});
export const resetEmrFormRulesQuerySchema = z.object({
    context: emrContextSchema,
    departmentId: z.string().uuid().optional(),
    formKey: z.string().trim().min(1).max(60).optional(),
});
export function emrRuleProblems(context, rules, reset) {
    const problems = [];
    const seen = new Set();
    rules.forEach((rule, index) => {
        const path = `rules.${index}`;
        const definition = emrFormDefinition(rule.formKey);
        if (!definition) {
            problems.push({ path: `${path}.formKey`, message: 'That form is not in the EMR form list' });
            return;
        }
        if (seen.has(rule.formKey))
            problems.push({ path: `${path}.formKey`, message: 'The same form is listed twice' });
        seen.add(rule.formKey);
        if (!definition.contexts.includes(context)) {
            problems.push({ path: `${path}.formKey`, message: 'That form is not used in this kind of visit' });
            return;
        }
        if (rule.required && !rule.enabled)
            problems.push({ path: `${path}.required`, message: 'A form that is switched off cannot be required' });
        if (definition.lockedRequired && (!rule.enabled || !rule.required)) {
            problems.push({ path: `${path}.required`, message: 'This form is always required and cannot be switched off' });
        }
    });
    reset.forEach((key, index) => {
        const definition = emrFormDefinition(key);
        if (!definition)
            problems.push({ path: `reset.${index}`, message: 'That form is not in the EMR form list' });
        else if (!definition.contexts.includes(context))
            problems.push({ path: `reset.${index}`, message: 'That form is not used in this kind of visit' });
        else if (seen.has(key))
            problems.push({ path: `reset.${index}`, message: 'A form cannot be changed and reset in the same save' });
    });
    return problems;
}
//# sourceMappingURL=emr-forms.js.map